<template>
  <div
    :class="{ collapse: isCollapse }"
    class="side-cart-container"
  >
    <div
      :class="{ collapse: isCollapse }"
      class="collapse-box"
    >
      <img
        @click="handleCollapse"
        :class="{ rotate: !isCollapse }"
        class="collapse-icon"
        src="@/assets/images/icon-collapse.svg"
        alt=""
      >
    </div>
    <div
      v-show="isCollapse"
      class="collapse-content"
    >
      <div class="my-cart">
        <img
          src="@/assets/illus/illus-my-cart.svg"
          alt=""
        >
        <div class="my-cart-text">
          My Cart
          <div
            v-if="cartCount > 0"
            class="cart-count"
          >
            {{ cartCount > 10 ? '10+' : cartCount }}
          </div>
        </div>
      </div>
    </div>
    <div
      :style="{ opacity: isCollapse ? 0 : 1 }"
      class="expand-content"
    >
      <div class="header">
        <div class="sub-total">
          Subtotal
        </div>
        <div class="total-price">
          <IntlCurrency
            :count="topSubtotal"
            :size="16"
            :currency="currencyData?.targetCurrency.slice(0, 2)"
            color="#232323"
          />
        </div>
        <div
          @click="handleCheckout"
          class="check-btn"
        >
          Checkout
        </div>
        <div
          @click="toCartPage"
          class="toCart-btn"
        >
          Go to cart
        </div>
      </div>
      <div class="split-line" />
      <div class="tab-container">
        <div
          v-for="(item, index) in tabList"
          @click="onTabChange(index)"
          class="tab-item"
          :class="{ active: checkedTabIndex === index }"
        >
          {{ item }}
        </div>
      </div>
      <div
        class="split-line"
        style="margin-top: 8px; margin-bottom: 8px;"
      />
      <div class="sku-list">
        <div
          v-for="sku in skuList"
          :key="sku.skuId"
          class="sku-item"
          :class="{ processingBg: sku.cartStatus === 0 }"
        >
          <div class="sku-image-wrapper">
            <Image
              class="sku-image"
              :style="{ opacity: isUnavailable(sku.cartStatus) ? '.4' : '' }"
              :src="sku.skuImage"
              alt=""
            />

            <img
              class="checked"
              v-if="isUnavailable(sku.cartStatus)"
              src="@/assets/H5/carts/disabled-check.svg"
              alt=""
            >
            <template v-else>
              <img
                v-if="sku.checked === true"
                @click="handleSelect(sku)"
                class="checked"
                src="@/assets/order/sku-selected.svg"
                alt=""
              >
              <img
                v-else
                @click="handleSelect(sku)"
                class="unselect"
                src="@/assets/order/sku-unselect.svg"
                alt=""
              >
            </template>
            <div
              v-if="sku.cartStatus === 0"
              class="processing-flag"
            >
              <img
                src="@/assets/images/processing-icon.svg"
                alt=""
              >
              Processing
            </div>
          </div>
          <div
            class="sku-attr"
            :style="{ color: isUnavailable(sku.cartStatus) ? '#B2B7C2' : '#6E717A' }"
            :title="sku.skuSpec"
          >
            {{ formatAttr(sku.skuSpec) }}
          </div>
          <div class="sku-price">
            <IntlCurrency
              :count="(bankersRound(currencyData?.rate * sku.skuPriceRmb, 2) * sku.skuQuantity).toFixed(2)"
              :size="14"
              :color="calcColor(sku.cartStatus)"
              :currency="currencyData.targetCurrency.slice(0, 2)"
            />
          </div>
          <div class="sku-options">
            <div class="counts-wrapper">
              <NumberInput
                :min="1"
                :disabled-minus="getSpuBatchNum(sku.spuId) >= getSpuQuantity(sku.spuId) || isUnavailable(sku.cartStatus) || sku.skuQuantity <= sku.bundleQuantity"
                :disabled-plus="isUnavailable(sku.cartStatus)"
                @change="handleManual(sku)"
                @minus="handleMinus(sku)"
                @plus="handlePlus(sku)"
                v-model="sku.skuQuantity"
              />
            </div>
            <div
              @click="handleDelete(sku)"
              @mouseenter="sku.isDeleteHover = true"
              @mouseleave="sku.isDeleteHover = false"
              class="delete-icon"
            >
              <img
                v-if="sku.isDeleteHover"
                src="@/assets/order/delete-icon-hover.svg"
                width="20px"
                alt=""
              >
              <img
                v-else
                src="@/assets/order/delete-icon.svg"
                width="20px"
                alt=""
              >
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="isEmpty"
        class="empty-cart"
      >
        <img
          src="@/assets/order/empty-cart.svg"
          alt=""
        >
        <div class="empty-text">
          Your cart is empty
        </div>
        <div
          v-if="!user.isLogged"
          @click="handleLogin"
          class="sign-text"
        >
          Hello,Sign In
        </div>
      </div>
    </div>

    <ProcessingDialog
      :visible="showProcessingDialog"
      @close="showProcessingDialog = false"
    />

    <AddressInfoDialog
      v-if="showAddressInfoDialog"
      :address-type="2"
      :current-country="currentCountry"
      :handle-close="handleCloseAddressInfoDialog"
      @click="stopClick"
      @save-address="saveAddress"
    />

    <ExceedQuantityDialog
      v-if="showExceedQuantityDialog"
      @close="handleCloseExceedQuantityDialog"
      :quantity="minOrderQuantity"
    />
  </div>
</template>

<script setup>
import { onMounted, ref, computed } from 'vue'
import { fetchData } from '@/utils/fetch'
import { bankersRound, gaAnalytics, buriedPointUtil, debounce } from '@/utils/utils'
import { usePlugin } from '@/hooks/usePlugin'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import ProcessingDialog from '../../../../Carts/components/CartsPC/components/ProcessingDialog.vue'
import { useUserStore } from '@/pinia/user'
import { useLoginModal } from '@/components/Login'
import AddressInfoDialog from '@/components/Offer/Sourcing/components/AddressInfoDialog/index.vue'
import ExceedQuantityDialog from '@/components/ExceedQuantityDialog/index.vue'
import { Image } from '@/components/Image'
import storage from '@/utils/storage'

const { $toast, $confirm } = usePlugin()

const store = useStore()
const user = useUserStore()
const { open: openLoginModal } = useLoginModal()

const router = useRouter()

const skuList = ref([])

const isEmpty = ref(false)

const currencyData = ref(null)

const showProcessingDialog = ref(false)

const showExceedQuantityDialog = ref(false)

const minOrderQuantity = ref(0)

const selectedSkuList = computed(() => skuList.value.filter(item => item.checked))

const topSubtotal = computed(() => {
  let total = 0
  skuList.value.forEach((v) => {
    if (v.checked) {
      total += bankersRound(currencyData?.value.rate * v.skuPriceRmb, 2) * v.skuQuantity
    }
  })
  return total.toFixed(2)
})

const cartCount = computed(() => store.state.cart.cartCount)

const isCollapse = ref(false)
const handleCollapse = () => {
  isCollapse.value = !isCollapse.value
}

const calcColor = (status) => {
  if (status === 0) return '#DB7E25'
  if (isUnavailable(status)) return '#6E717A'
  return '#232323'
}

const formatAttr = (string) => {
  const arr = string.split(';')
  return arr.map(item => {
    const [key, value] = item.split(':')
    return `${value}`
  }).join(', ')
}

const handleCheckout = () => {
  if (selectedSkuList.value.length === 0) {
    return $toast('Please select at least one product', { type: 'error' })
  }

  const hasProcessing = selectedSkuList.value.some(item => item.cartStatus === 0)
  if (hasProcessing) {
    showProcessingDialog.value = true
    return
  }

  gaAnalytics('P_Checkout', 'P_Checkout')
  gaAnalytics('p_checkout_click', 'p_checkout_click')
  buriedPointUtil({ eventId: 'PC204' })

  // 检查每个SPU是否满足最小批量
  const spuIds = [...new Set(selectedSkuList.value.map((item) => item.spuId))]
  for (const spuId of spuIds) {
    const batchNum = getSpuBatchNum(spuId)
    const quantity = getSpuQuantity(spuId)
    if (quantity < batchNum) {
      $toast('Please meet the minimum order quantity for all items.', { type: 'error' })
      return
    }
  }

  sessionStorage.setItem('carts', JSON.stringify(selectedSkuList.value.map(item => item.cartId)))
  router.push('/orderFilling')
}

const toCartPage = () => {
  buriedPointUtil({ eventId: 'PC202' })
  router.push('/carts')
}

const handleSelect = (sku) => {
  const checked = sku.checked
  if (checked) {
    sku.checked = false
    selectedSkuList.value = selectedSkuList.value.filter(item => item.skuId !== sku.skuId)
  } else {
    sku.checked = true
    selectedSkuList.value.push(sku)
  }
  updateProductSkuPrice(sku)
}

const getSpuBatchNum = (spuId) => {
  const spuItems = skuList.value.filter((item) => item.spuId === spuId)
  return Math.max(...spuItems.map((item) => item.batchNum))
}

const getSpuQuantity = (spuId) => {
  const spuItems = skuList.value.filter((item) => item.spuId === spuId)
  return spuItems.reduce((sum, item) => sum + Number(item.skuQuantity), 0)
}

const handleDelete = (item) => {
  $confirm('Delete item?', 'Are you sure you want to delete itme from cart?').then(async () => {
    const { code, data } = await fetchData('/appb/b2b/cart/cartDelete', {
      method: 'post',
      body: JSON.stringify({
        cartIds: [item.cartId]
      })
    })
    if (code === 200) {
      $toast('Deleted successfully.', { type: 'success' })
      skuList.value = skuList.value.filter((v) => v.cartId !== item.cartId)

      fetchData('/appb/b2b/cart/cartCount').then((res) => {
        if (res.code === 200) {
          store.commit('cart/SET_CART_COUNT', res.data)
        }
      })
    }
  })
}

// 捆绑数量算法
const calcBundleQuantity = (data) => {
  const bundleQuantity = +data.bundleQuantity
  if (bundleQuantity === 1) return

  const halfBundle = bundleQuantity / 2
  let quantity = data.skuQuantity
  quantity = (Math.floor((quantity + halfBundle) / bundleQuantity)) * bundleQuantity
  if (quantity <= 0) {
    quantity = bundleQuantity
  }
  data.skuQuantity = quantity
}

const handleMinus = (item) => {
  const spuQuantity = getSpuQuantity(item.spuId)
  const spuBatchNum = getSpuBatchNum(item.spuId)

  if (spuQuantity > spuBatchNum && item.skuQuantity >= 1) {
    item.skuSubtotal = (bankersRound(currencyData?.value.rate * item.skuPriceRmb, 2) * item.skuQuantity).toFixed(2)
    item.skuQuantity++
    item.skuQuantity -= item.bundleQuantity
    updateProductSkuPrice(item)

    minOrderQuantity.value = item.batchNum
    updateCarts({
      cartId: item.cartId,
      quantity: item.skuQuantity
    })
  }
}

const handlePlus = (item) => {
  item.skuQuantity--
  item.skuQuantity += item.bundleQuantity
  item.skuSubtotal = (bankersRound(currencyData?.value.rate * item.skuPriceRmb, 2) * item.skuQuantity).toFixed(2)
  minOrderQuantity.value = item.batchNum
  updateProductSkuPrice(item)

  updateCarts({
    cartId: item.cartId,
    quantity: item.skuQuantity
  })
}

const handleManual = debounce((data) => {
  calcBundleQuantity(data)
  minOrderQuantity.value = data.batchNum
  if (Number(data.skuQuantity) > 0) {
    updateCarts(data.cartId, data.skuQuantity)
  } else {
    data.skuQuantity = 1
    updateCarts(data.cartId, 1)
  }
  updateProductSkuPrice(data)
}, 300)

const updateCarts = async (params) => {
  const { code } = await fetchData('/appb/b2b/cart/cartUpdate', {
    method: 'post',
    body: JSON.stringify(params)
  })

  if (code === 200001) {
    $toast('Please fill delivery address.')
    localStorage.removeItem('addressForm')
    showAddressInfoDialog.value = true
  } else if (code === 100001) {
    showExceedQuantityDialog.value = true
  }
}

// 获取购物车商品
const getCartSkuList = async (status = null) => {
  const url = status ? `/appb/b2b/cart/cartList?page=1&pageSize=200&frontCartStats=${status}` : '/appb/b2b/cart/cartList?page=1&pageSize=200'

  const { code, data } = await fetchData(url)
  if (code === 200) {
    const list = data.rows || []
    skuList.value = list.map((item, index) => {
      if (index < 50 && [0, 10].includes(item.cartStatus)) {
        item.checked = true
      }
      return item
    })
    skuList.value = list.map(item => {
      updateProductSkuPrice(item)
      item.skuSubtotal = (bankersRound(currencyData?.value.rate * item.skuPriceRmb, 2) * item.skuQuantity).toFixed(2)
      return item
    })

    if (skuList.value.length === 0) {
      isEmpty.value = true
    } else {
      isEmpty.value = false
    }
  }
}

const getCurrency = async () => {
  const { code, data } = await fetchData('/dsp-app/alibaba-common/currencyList')
  if (code === 200) {
    const _cache = storage.get('currencyData')
    if (_cache) {
      currencyData.value = data.find((item) => item.targetCurrency === _cache.targetCurrency)
    } else {
      currencyData.value = data[0]
    }
    storage.set('currencyData', currencyData.value)
    store.commit('setCurrencyData', currencyData.value)
  }
}

const handleLogin = () => {
  gaAnalytics('p_sign_in_click', 'p_sign_in_click')
  buriedPointUtil({ eventId: 'PWH201' })
  store.commit('setPassiveLogin', 0)
  openLoginModal()
}

// 获取起批量对应价格
const getPriceByQuantity = (quantity, priceRange) => {
  let validPrices = priceRange.filter(item => item.startQuantity <= quantity)
  if (validPrices.length === 0) {
    validPrices = [priceRange[0]]
  }

  const bestMatch = validPrices.reduce((prev, curr) =>
    curr.startQuantity > prev.startQuantity ? curr : prev
  )

  return Number(bestMatch.price)
}

// 更新同级商品下sku价格
const updateProductSkuPrice = (obj) => {
  // 先更新自身
  obj.skuPriceRmb = getPriceByQuantity(obj.skuQuantity, obj.skuPriceCnyRanges)

  // 单个区间 return
  const skuPriceCnyRanges = obj.skuPriceCnyRanges || []
  if (skuPriceCnyRanges.length < 2) return

  // 获取已选中的同个商品级别下的sku加购总数
  let count = 0
  const checkedList = skuList.value.filter(v => v.checked)
  const sameProductLevelSku = checkedList.filter(v => v.spuId === obj.spuId)
  sameProductLevelSku.forEach(item => {
    count += item.skuQuantity
  })

  if (count) {
    const skuPriceRmb = getPriceByQuantity(count, obj.skuPriceCnyRanges)
    // 同级sku同步更新金额
    sameProductLevelSku.forEach(item => item.skuPriceRmb = skuPriceRmb)
  }
}

const tabList = ref(['All Items', 'Available', 'Processing'])
const checkedTabIndex = ref(0)

const onTabChange = (index) => {
  checkedTabIndex.value = index
  const statusMap = {
    0: null,
    1: '2',
    2: '0'
  }
  getCartSkuList(statusMap[index])
}

const isUnavailable = (status) => {
  return [50, 51, 52].includes(Number(status))
}

// 地址弹窗
const currentCountry = ref({})
const countryList = computed(() => store.state.countryInfo)
const countryObj = computed(() => store.state.currentCountry)

watch(
  [countryList, countryObj],
  (newData) => {
    if (newData) {
      const country = localStorage.getItem('setCurrentCountry')
        ? JSON.parse(localStorage.getItem('setCurrentCountry'))
        : ''
      if (country) {
        currentCountry.value = country
        return
      }
      if (newData[1]) {
        currentCountry.value = newData[1]
        return
      }
      currentCountry.value = newData[0][0]
    }
  },
  { immediate: true }
)

const showAddressInfoDialog = ref(false)
const handleCloseAddressInfoDialog = () => {
  showAddressInfoDialog.value = false
}

const stopClick = (event) => {
  event.stopPropagation()
}

const saveAddress = () => {
  $toast('Address saved successfully', { type: 'success' })
  showAddressInfoDialog.value = false
}

const handleCloseExceedQuantityDialog = () => {
  showExceedQuantityDialog.value = false
  getCartSkuList()
}

defineExpose({
  getCartSkuList
})

onMounted(async () => {
  buriedPointUtil({ eventId: 'PC502' })
  await getCurrency()
  getCartSkuList()
})
</script>

<style lang='less' scoped>
.collapse {
  width: 80px !important;
}

.side-cart-container {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 144px;
  background-color: white;
  box-shadow: -4px 0 8px rgba(0, 0, 0, 0.1);
  overflow: auto;
  z-index: 9999;
  transition: width 0.3s;

  &::-webkit-scrollbar {
    display: none;
  }

  .collapse-box {
    position: fixed;
    top: 0;
    right: 0;
    width: 144px;
    height: 32px;
    background-color: #F4F7FF;
    border-bottom: 1px solid #d3e1fd;
    z-index: 999;
    transition: width 0.3s;

    .collapse-icon {
      position: absolute;
      top: 8px;
      left: 16px;
      transition: transform 0.3s;

      &.rotate {
        transform: rotate(180deg);
      }
    }
  }

  .collapse-content {
    position: relative;
    height: 100vh;
    .my-cart {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      .my-cart-text {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 2px;
        color: #232323;
        font-size: 12px;
        font-family: mon-600;
        .cart-count  {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background-color: #009de0;
          font-size: 10px;
          color: white;
        }
      }
    }
  }

  .expand-content {
    transition: opacity 0.3s;
    padding-top: 48px;
  }

  .header {
    width: 128px;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;

    .sub-total {
      color: #232323;
      font-size: 12px;
      font-family: mon-500;
      line-height: 18px;
    }
    .total-price {
      display: flex;
      align-items: center;
      font-family: mon-700;
      margin-top: 2px;
    }
    .check-btn, .toCart-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 32px;
      border-radius: 20px;
      background-color: #052784;
      color: white;
      font-size: 12px;
      font-family: mon-600;
      margin-top: 8px;
      cursor: pointer;
    }
    .toCart-btn {
      border: 1px solid #EBF1F5;
      color: #232323;
      background-color: white;
    }
  }
  .split-line {
    height: 1px;
    width: 128px;
    background-color: #ebf5f5;
    margin: 12px auto;
  }
  .tab-container {
    display: flex;
    flex-direction: column;
    gap: 8px;
    .tab-item {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      height: 30px;
      color: #5c5c5c;
      font-size: 12px;
      font-family: mon-500;
      cursor: pointer;

      &.active {
        background-color: #EFF4FD;
        color: #232323;

        &::after {
          content: '';
          top: 50%;
          transform: translateY(-50%);
          position: absolute;
          left: 0;
          width: 2px;
          height: 20px;
          background-color: #052784;
          border-radius: 0 2px 2px 0;
        }
      }
    }

  }
  .sku-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 0 8px;
    .sku-item {
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: 8px;

      &.processingBg {
        background-color: #FFFCF5;
      }

      .sku-image-wrapper {
        position: relative;
        width: 108px;
        height: 108px;
        border-radius: 4px;
        overflow: hidden;
        .sku-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        .unselect, .checked {
          position: absolute;
          top: 4px;
          left: 4px;
          cursor: pointer;
        }
        .processing-flag {
          position: absolute;
          bottom: 0;
          height: 24px;
          width: 100%;
          background-color: #FFA033;
          color: white;
          font-size: 11px;
          font-family: mon-500;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 4px;
        }
      }
      .sku-attr {
        color: #6E717A;
        font-size: 11px;
        font-family: mon-500;
        line-height: 16px;
        margin-top: 4px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        width: 100%;
        text-align: center;
        padding: 0 8px;
      }
      .sku-price {
        display: flex;
        align-items: center;
        color: #232323;
      }
      .sku-options {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 4px;
        margin-top: 8px;
        .counts-wrapper {
          width: 84px;
          height: 24px;
        }
        .delete-icon {
          height: 20px;
          cursor: pointer;
        }
      }
    }
  }
  .empty-cart {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    .empty-text {
      color: #232323;
      font-size: 11px;
      font-family: mon-500;
      line-height: 16px;
    }
    .sign-text {
      margin-top: 2px;
      color: #009de0;
      font-size: 11px;
      font-family: mon-500;
      line-height: 16px;
      cursor: pointer;
      &:hover {
        text-decoration: underline;
      }
    }
  }
}
</style>
